import { Field, getIn, useFormikContext } from 'formik';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { useTheme } from 'styled-components';

import {
  ADDITIONAL_INFORMATION_STATUS,
  AdditionalInformation,
} from '@hivebrite/design-system/AdditionalInformation';
import { Box } from '@hivebrite/design-system/Box';
import { Label } from '@hivebrite/design-system/Label';
import { ScreenReadersContent } from '@hivebrite/design-system/ScreenReadersContent';
import { POSITIONS } from '@hivebrite/design-system/Tooltip';
import { getErrorId, getTooltipId } from '@hivebrite/utils';

import getSelectComponent from 'hb-react/shared/components/formik/utils/getSelectComponent';
import { getOptionValue as defaultGetOptionValue } from 'hb-react/shared/components/ReactSelect/utils/selectValues';

import { validateSelect } from './validate';

const Select = ({
  ariaDescribedBy,
  ariaLabel,
  autoComplete = false,
  designSystemStyle,
  disabled,
  getOptionValue = defaultGetOptionValue,
  hideError = false,
  id,
  info = undefined, // to avoid adding @ts-expect-error for info={undefined} in every file using Select.jsx
  infoIcon = undefined, // to avoid adding @ts-expect-error for infoIcon={undefined} in every file using Select.jsx
  inputHtml = {},
  isClearable,
  isMulti = false,
  isNotSearchable,
  label,
  labelMultiline = true,
  labelPosition,
  menuPlacement = 'auto',
  menuPortalTarget = undefined, // to avoid adding @ts-expect-error for menuPortalTarget={undefined} in every file using Select.jsx
  menuPosition = 'absolute',
  menuShouldBlockScroll = false,
  name,
  notCloseMenuOnSelect,
  onBlur,
  onChange,
  options,
  placeholder,
  tooltip,
  tooltipPosition = POSITIONS.BOTTOM,
  type = 'default',
  validate = {},
  wrapperProps = {},
}) => {
  const {
    errors,
    values,
    touched,
    setFieldValue,
    setFieldTouched,
    submitCount,
  } = useFormikContext();
  const { required } = validate;
  const theme = useTheme();
  const SelectComponent = getSelectComponent(type);
  const htmlId = id || name;
  const formikValue = getIn(values, name);
  const formikError = getIn(errors, name);
  const formikTouched = getIn(touched, name);

  const tooltipId = useMemo(() => getTooltipId(htmlId), [htmlId]);
  const errorId = useMemo(() => getErrorId(htmlId), [htmlId]);

  const handleChange = (selectedOption) => {
    const newValue = selectedOption
      ? Array.isArray(selectedOption)
        ? selectedOption.map((option) => getOptionValue(option))
        : getOptionValue(selectedOption)
      : '';

    setFieldValue(name, newValue);

    if (onChange) {
      onChange(selectedOption);
    }
  };

  // When user selects an option, ReactSelect triggers change event and, in
  // some cases, blur event as well. This triggers validation of the Formik
  // form when the value state is still not updated. As the result user will
  // see the error message if previous value for the select is not valid.
  //
  // This debounce ensures that validation is only triggered after the value
  // state is updated.
  const handleBlur = debounce(() => {
    setFieldTouched(name, true);
    onBlur?.();
  }, 0);

  return (
    <Box mb={6} display="block" {...wrapperProps}>
      <Box
        display="block"
        opacity={disabled ? theme.input.disabled.opacity : 1}
        {...(labelPosition === 'left' && {
          gridColumnGap: '8px',
          alignItems: 'center',
          display: 'flex',
        })}
      >
        {label && (
          <Label
            id={`${htmlId}-input`}
            disabled={disabled}
            multiline={labelMultiline}
            required={required}
            tooltip={tooltip}
            initialPosition={tooltipPosition}
          >
            {label}
          </Label>
        )}
        <Field
          name={name}
          validate={(value) =>
            validateSelect({
              required,
              value,
            })
          }
        >
          {() => (
            <SelectComponent
              ariaDescribedBy={ariaDescribedBy}
              // Note - describedById is not align with the rest of the App (should be ariaDescribedBy). To be updated
              {...((formikError || ariaDescribedBy || tooltip) && {
                describedById: [
                  formikError && errorId,
                  ariaDescribedBy,
                  tooltip && tooltipId,
                ]
                  .filter(Boolean)
                  .join(' '),
              })}
              ariaLabel={ariaLabel}
              designSystemStyle={designSystemStyle}
              disabled={disabled}
              error={(submitCount || formikTouched) && formikError}
              id={htmlId}
              isClearable={isClearable}
              isMulti={isMulti}
              isNotSearchable={isNotSearchable}
              name={name}
              menuPlacement={menuPlacement}
              menuPosition={menuPosition}
              menuPortalTarget={menuPortalTarget ?? document.body}
              notCloseMenuOnSelect={notCloseMenuOnSelect}
              onBlur={handleBlur}
              onChange={handleChange}
              options={options}
              placeholder={placeholder}
              value={formikValue}
              autoComplete={autoComplete}
              menuShouldBlockScroll={menuShouldBlockScroll}
              hideError
              // label is handled internally in this component, force the children label to be empty
              label=""
              {...inputHtml}
            />
          )}
        </Field>
      </Box>

      {/* Make tooltip also available for ScreenReaders */}
      {tooltip && (
        <ScreenReadersContent id={tooltipId}>
          {typeof tooltip === 'function' ? tooltip() : tooltip}
        </ScreenReadersContent>
      )}

      {info && typeof info === 'string' && (
        <AdditionalInformation
          iconName={infoIcon}
          id={id}
          status={ADDITIONAL_INFORMATION_STATUS.INFO}
        >
          {info}
        </AdditionalInformation>
      )}

      {!hideError && (formikTouched || submitCount > 0) && formikError && (
        <AdditionalInformation
          id={htmlId}
          status={ADDITIONAL_INFORMATION_STATUS.ERROR}
        >
          {formikError}
        </AdditionalInformation>
      )}
    </Box>
  );
};

Select.propTypes = {
  designSystemStyle: PropTypes.bool,
  name: PropTypes.string.isRequired,
  ariaDescribedBy: PropTypes.string,
  ariaLabel: PropTypes.string,
  disabled: PropTypes.bool,
  getOptionValue: PropTypes.func,
  hideError: PropTypes.bool,
  id: PropTypes.string,
  inputHtml: PropTypes.object,
  isClearable: PropTypes.bool,
  isNotSearchable: PropTypes.bool,
  label: PropTypes.string,
  labelMultiline: PropTypes.bool,
  menuPlacement: PropTypes.string,
  menuPosition: PropTypes.oneOf(['absolute', 'fixed']),
  notCloseMenuOnSelect: PropTypes.bool,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  options: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  placeholder: PropTypes.string,
  tooltip: PropTypes.string,
  type: PropTypes.oneOf([
    'codeAPE',
    'companies',
    'countries',
    'default',
    'industries',
    'job_functions',
    'skills',
    'timezone',
    'users',
    'mentors',
    'mentees',
  ]),
  validate: PropTypes.shape({
    required: PropTypes.bool,
  }),
  wrapperProps: PropTypes.object,
  autocomplete: PropTypes.bool,
  menuShouldBlockScroll: PropTypes.bool,
  labelPosition: PropTypes.oneOf(['left', 'top']),
};

export default Select;
